// QR Kiosk v2 — KV2-9 Tier 0 digital nameplate. The ANONYMOUS page a QR scan lands on:
// one route, one GET, read-only. IDENTITY ONLY — never condition, health, fault counts,
// open-report counts, live status, alarms, GUIDs, documents (owner ruling D14, 2026-08-13:
// login-only), or owner PII of any kind (owner ruling D15, 2026-08-14: the owner contact
// row is cut and the owner block leaves the wire; it returns only behind the D8 per-tenant
// opt-in gate). B7 plan §1 / §5.4.
//
// The page is exactly three things: the chrome band (wordmark, language, tier chip), the
// access pills, and the plate. See the D15 guard comment on the loaded branch below.
//
// KioskV2Layout mounts INSIDE the auth gate, so this page cannot use it. It carries the
// shipped anonymous-shell pattern from guest/GuestEntryV2.tsx instead: side-effect import
// of the token sheet + its own scoped root — here with .tier-anon (slate accent), not
// .tier-guest. No Redux: this page shares no state with the kiosk and must not touch
// publicSlice (whose thunks call authenticated routes).
import "../kiosk-v2-tokens.css";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useBreakpoint } from "../../../shared/hooks/useBreakpoint";
import useTranslation from "../../../shared/hooks/useTranslation";
import { getNameplate, nameplateErrorKind, NameplateErrorKind } from "./nameplateApi";
import { Nameplate } from "./nameplateModels";
import { NameplateEscalation } from "./NameplateLadder";
import { NameplateLanguageSelect } from "./NameplateLanguageSelect";
import { NameplateSkeleton } from "./NameplateSkeleton";
import { NameplateUnavailable } from "./NameplateUnavailable";
import { PlateCard } from "./PlateCard";

// Compact chrome wordmark at the artboard's Sora extrabold (800). The previous pass had to
// settle for 700 because index.html loaded Sora 300–700 only; 800 is now on the wire (it is
// the same variable file, so the extra weight is free) and this renders a REAL extrabold
// face rather than a clamped 700. Do not walk this back to 700 without also checking the
// font link — the two have to move together.
function Brand() {
  return (
    <span
      style={{
        fontWeight: 800,
        fontSize: 13.5,
        lineHeight: "18px",
        letterSpacing: "0.02em",
        color: "var(--ink)",
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      MACHINE
      <b
        style={{
          color: "var(--card)",
          background: "var(--ink)",
          padding: "2px 4px",
          borderRadius: 4,
          fontSize: 11.5,
          lineHeight: "14px",
          fontWeight: 800,
        }}
      >
        ID
      </b>
    </span>
  );
}

/** The anon tier pill, inlined from the designer kiosk-shared.jsx TierChip kind="anon".
 *  chrome/TierChip.tsx is deliberately NOT imported — it is the Tier-2 identity chip and
 *  expects kiosk layout context (it dispatches openAccountSheet). */
function AnonChip({ label }: { label: string }) {
  // L6: was nowrap + fixed 26px height, so DE "ÖFFENTLICHES TYPENSCHILD" (179px) pushed the
  // chrome row 33px past a 320px viewport. The chip may now shrink and wrap to two lines
  // (minHeight keeps the 26px single-line look whenever the label fits — every language at
  // >= 375px; only narrower phones see DE/PL on two lines).
  return (
    <span
      className="kv2-hy"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        minHeight: 26,
        padding: "4px 10px",
        borderRadius: 999,
        minWidth: 0,
        lineHeight: "11px",
        background: "var(--tier-accent-soft)",
        border: "1px solid var(--tier-accent-line)",
        fontFamily: "var(--font-mono)",
        fontSize: 9,
        fontWeight: 600,
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        color: "var(--tier-accent)",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 999, background: "var(--tier-accent)", flexShrink: 0 }} />
      <span style={{ minWidth: 0 }}>{label}</span>
    </span>
  );
}

export function NameplateV2() {
  const { code } = useParams();
  const { translate } = useTranslation("public");
  const { isPhone } = useBreakpoint();

  const [data, setData] = useState<Nameplate | null>(null);
  const [errorKind, setErrorKind] = useState<NameplateErrorKind | null>(null);

  // FETCH EXACTLY ONCE PER CODE. Every 200 writes a NameplateScanEvent server-side and the
  // per-machine scan count is the Tajmac pilot KPI — a second GET is a double-counted scan,
  // i.e. a data-quality bug, not a cosmetic one. This ref survives a re-render AND React
  // StrictMode's deliberate double-invoke of effects in development.
  const fetchedRef = useRef<string | null>(null);

  const load = useCallback((c: string) => {
    setData(null);
    setErrorKind(null);
    getNameplate(c)
      .then((result) => {
        // Ignore a resolution for a code we have since navigated away from.
        if (fetchedRef.current !== c) return;
        setData(result);
      })
      .catch((err) => {
        if (fetchedRef.current !== c) return;
        setErrorKind(nameplateErrorKind(err));
      });
  }, []);

  useEffect(() => {
    if (!code) return;
    if (fetchedRef.current === code) return;
    fetchedRef.current = code;
    load(code);
  }, [code, load]);

  // Retry is an explicit user action on a FAILED load, so re-issuing the GET is intended
  // (a failed request wrote no scan event).
  const retry = useCallback(() => {
    if (code) load(code);
  }, [code, load]);

  // §8 — one column at every width; the only breakpoint-driven values are the column
  // max-width and the phone side padding. Never useMediaQuery.
  const columnMaxWidth: number | string = isPhone ? "100%" : 460;

  // Every state is one 12px-gapped column so no state can drift into a different rhythm.
  const stack: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 12, minWidth: 0 };

  let body: React.ReactNode;
  if (errorKind) {
    body = <NameplateUnavailable kind={errorKind} onRetry={retry} />;
  } else if (!data) {
    body = <NameplateSkeleton />;
  } else {
    body = (
      <div className="kv2-fade" style={stack}>
        {/* Page = chrome + access pills + the plate. NOTHING renders below the plate — owner ruling 2026-08-14 (D15). Do not re-add ladders, status lines, or contact rows without a new owner ruling; owner contact returns only with the D8 opt-in gate. */}
        <NameplateEscalation machineNo={data.machineNo} />

        <PlateCard data={data} />
      </div>
    );
  }

  return (
    // .p-claude, not .p-machineid: the N2 canvas is drawn in the warm registry-paper palette,
    // which this sheet already carries verbatim as P1 "Refined Claude" (page #F7F5EE, card
    // #FBFAF5, inset #F1EEE4, hairlines #EFEBDF/#E6E1D2). Every canvas hex resolves to a
    // token in .p-claude + .tier-anon, so the page needs no palette of its own.
    <div
      className="qr-kiosk-v2-root p-claude tier-anon"
      style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "var(--bg)", width: "100%" }}
    >
      {/* Chrome band — full-bleed card surface with a hairline foot, inner row aligned to
          the same column as the content. The "EN ▾" the canvas always drew is now REAL
          (owner ruling D16, 2026-08-14): an anonymous scanner has no route to the kiosk
          settings surface, so the language control has to live here. It is the page-local
          NameplateLanguageSelect, not chrome/LanguageChip — that shared chip is a no-op off
          kiosk machine routes and has other consumers (see the header of the new file). */}
      <div style={{ width: "100%", background: "var(--card)", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
        <div
          style={{
            width: "100%",
            maxWidth: columnMaxWidth,
            margin: "0 auto",
            height: 50,
            padding: "0 14px",
            display: "flex",
            alignItems: "center",
            gap: 10,
            minWidth: 0,
          }}
        >
          <Brand />
          <span style={{ flex: 1 }} />
          <NameplateLanguageSelect />
          <AnonChip label={translate("kioskV2NameplateTierChip")} />
        </div>
      </div>

      <div
        style={{
          width: "100%",
          maxWidth: columnMaxWidth,
          margin: "0 auto",
          padding: "12px 14px calc(20px + env(safe-area-inset-bottom))",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minWidth: 0,
        }}
      >
        {body}
      </div>
    </div>
  );
}

export default NameplateV2;
